import { Link } from "react-router";
import { useShopCategories, useShopFeatured, useShopProducts } from "@/lib/api.ts";
import { ProductCard } from "@/components/ProductCard.tsx";
import { TestimonialsSection } from "@/components/TestimonialsSection.tsx";
import { BrandValuesSection } from "@/components/BrandValuesSection.tsx";
import { BlogPreviewSection } from "@/components/BlogPreviewSection.tsx";
import { toUrlFriendly, stripHtml, formatPrice } from  "@/lib/utils.ts";
import { ArrowRight, Truck, Shield, Award, Check } from "lucide-react";
import { useDocumentHead } from "@/hooks/useDocumentHead.ts";
import type { Product } from "@/lib/api.ts";

export function HomePage() {
  useDocumentHead({
    title: "The Secret Trends",
    description: "Discover trending products with expert reviews and exclusive deals. Shop the latest in beauty, health, tech, and lifestyle at The Secret Trends.",
  });
  const { data: catData } = useShopCategories();
  const { data: featuredData } = useShopFeatured(28);
  const { data: allData } = useShopProducts();

  const categories = catData?.categories ?? [];
  const featured = featuredData?.products ?? [];
  const allProducts = allData ?? {};
  const categoryNames = Object.keys(allProducts);

  // Split featured products for different sections
  const heroProduct = featured[0] ?? null;
  const gridProducts = featured.slice(1, 5);
  const spotlightProduct = featured[5] ?? featured[1] ?? null;
  const secondGridProducts = featured.slice(6, 10);
  const bannerProduct = featured[10] ?? featured[2] ?? null;

  // Get first two categories that have products for category spotlights
  const catWithProducts = categoryNames
    .map((name) => ({ name, products: allProducts[name] ?? [] }))
    .filter((c) => c.products.length >= 2);

  return (
    <div>
      {/* ── Hero — Full-width product spotlight ──────────────── */}
      {heroProduct && (
        <section className="bg-bg-alt">
          <div className="mx-auto grid max-w-[1360px] items-center gap-8 px-6 py-16 lg:grid-cols-2 lg:gap-16 lg:px-10 lg:py-24">
            <div className="flex items-center justify-center">
              {heroProduct.imageUrl && (
                <div className="flex w-full items-center justify-center rounded-2xl bg-white p-8 lg:p-12">
                  <img
                    src={heroProduct.imageUrl}
                    alt={heroProduct.name}
                    className="max-h-[420px] w-full object-contain"
                  />
                </div>
              )}
            </div>
            <div>
              {heroProduct.category && (
                <span className="text-xs font-medium uppercase tracking-widest text-text-muted">
                  {heroProduct.category}
                </span>
              )}
              <h1 className="mt-2 text-[36px] font-medium leading-tight tracking-tight text-heading lg:text-[48px]">
                {heroProduct.name}
              </h1>
              {heroProduct.richDesc && (
                <p className="mt-4 max-w-lg text-[15px] leading-[1.7] text-text-muted line-clamp-3">
                  {stripHtml(heroProduct.richDesc)}
                </p>
              )}
              {(() => {
                const highlights = [...(heroProduct.features ?? []), ...(heroProduct.benefits ?? [])].slice(0, 3);
                return highlights.length > 0 ? (
                  <ul className="mt-5 space-y-2">
                    {highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-[14px] text-text-muted">
                        <Check size={15} className="mt-0.5 flex-shrink-0 text-heading" />
                        <span>{typeof h === "string" ? h : String(h)}</span>
                      </li>
                    ))}
                  </ul>
                ) : null;
              })()}
              {heroProduct.price && (
                <p className="mt-5 text-2xl font-semibold text-heading">
                  {formatPrice(heroProduct.price)}
                </p>
              )}
              <Link
                to={`/product/${heroProduct.slug}`}
                className="mt-8 inline-flex items-center gap-2 bg-[#1A1A1A] px-8 py-4 text-sm font-medium tracking-wide text-white transition-colors hover:bg-[#333]"
              >
                Shop Now
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── Product Grid — 4 columns ────────────────────────── */}
      {gridProducts.length > 0 && (
        <section className="py-20">
          <div className="mx-auto max-w-[1360px] px-6 lg:px-10">
            <h2 className="mb-10 text-[28px] font-medium tracking-tight text-heading">
              Trending Now
            </h2>
            <div className="grid grid-cols-2 gap-6 lg:grid-cols-4 lg:gap-8">
              {gridProducts.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Full-width Banner — Single product spotlight ───── */}
      {spotlightProduct && (
        <ProductBanner product={spotlightProduct} variant="dark" />
      )}

      {/* ── Category row — compact inline chips ─────────────── */}
      {categories.length > 0 && (
        <section className="py-16">
          <div className="mx-auto max-w-[1360px] px-6 lg:px-10">
            <div className="flex flex-wrap items-center gap-3">
              <span className="mr-4 text-sm font-medium text-heading">
                Shop by Category
              </span>
              {categories.map((cat) => {
                const name = cat.category ?? "Other";
                return (
                  <Link
                    key={name}
                    to={`/collection/${toUrlFriendly(name)}`}
                    className="border border-border px-5 py-2.5 text-xs font-medium tracking-wide text-heading transition-all hover:border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white"
                  >
                    {name}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Two-up category blocks (Cybex-style split) ──────── */}
      {catWithProducts.length >= 2 && (
        <section>
          <div className="grid lg:grid-cols-2">
            {catWithProducts.slice(0, 2).map(({ name, products }) => {
              const topProduct = products[0];
              return (
                <Link
                  key={name}
                  to={`/collection/${toUrlFriendly(name)}`}
                  className="group relative flex min-h-[400px] items-end bg-bg-alt p-8 lg:min-h-[500px] lg:p-12"
                >
                  {topProduct?.imageUrl && (
                    <img
                      src={topProduct.imageUrl}
                      alt={name}
                      className="absolute inset-0 h-full w-full object-contain p-12 opacity-40 transition-transform duration-500 group-hover:scale-105"
                    />
                  )}
                  <div className="relative z-10">
                    <p className="text-xs font-medium uppercase tracking-widest text-text-muted">
                      {products.length} products
                    </p>
                    <h3 className="mt-1 text-2xl font-medium text-heading lg:text-3xl">
                      {name}
                    </h3>
                    <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-heading">
                      Explore
                      <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Second product grid ─────────────────────────────── */}
      {secondGridProducts.length > 0 && (
        <section className="py-20">
          <div className="mx-auto max-w-[1360px] px-6 lg:px-10">
            <h2 className="mb-10 text-[28px] font-medium tracking-tight text-heading">
              More to Discover
            </h2>
            <div className="grid grid-cols-2 gap-6 lg:grid-cols-4 lg:gap-8">
              {secondGridProducts.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Testimonials ─────────────────────────────────────── */}
      <TestimonialsSection />

      {/* ── Full-width Banner — another product ─────────────── */}
      {bannerProduct && bannerProduct.id !== spotlightProduct?.id && (
        <ProductBanner product={bannerProduct} variant="light" />
      )}

      {/* ── Three-up category blocks ────────────────────────── */}
      {catWithProducts.length >= 3 && (
        <section className="py-20">
          <div className="mx-auto max-w-[1360px] px-6 lg:px-10">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {catWithProducts.slice(2, 5).map(({ name, products }) => {
                const topProduct = products[0];
                return (
                  <Link
                    key={name}
                    to={`/collection/${toUrlFriendly(name)}`}
                    className="group relative flex min-h-[320px] flex-col justify-end overflow-hidden bg-bg-alt p-8"
                  >
                    {topProduct?.imageUrl && (
                      <img
                        src={topProduct.imageUrl}
                        alt={name}
                        className="absolute inset-0 h-full w-full object-contain p-8 opacity-30 transition-transform duration-500 group-hover:scale-105"
                      />
                    )}
                    <div className="relative z-10">
                      <h3 className="text-xl font-medium text-heading">{name}</h3>
                      <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-text-muted">
                        Shop now <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Brand Values ──────────────────────────────────────── */}
      <BrandValuesSection />

      {/* ── Remaining products as a final grid ──────────────── */}
      {featured.length > 10 && (
        <section className="bg-bg-alt py-20">
          <div className="mx-auto max-w-[1360px] px-6 lg:px-10">
            <h2 className="mb-10 text-[28px] font-medium tracking-tight text-heading">
              Just for You
            </h2>
            <div className="grid grid-cols-2 gap-6 lg:grid-cols-4 lg:gap-8">
              {featured.slice(11).map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Blog Preview ──────────────────────────────────────── */}
      <BlogPreviewSection />

      {/* ── Trust Bar ───────────────────────────────────────── */}
      <section className="border-t border-border py-12">
        <div className="mx-auto flex max-w-[1360px] flex-col items-center justify-center gap-8 px-6 sm:flex-row sm:gap-16 lg:px-10">
          <div className="flex items-center gap-3 text-center sm:text-left">
            <Truck size={24} className="flex-shrink-0 text-text-muted" />
            <div>
              <p className="text-sm font-medium text-heading">Free Shipping Info</p>
              <p className="text-xs text-text-muted">On qualifying orders</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-center sm:text-left">
            <Shield size={24} className="flex-shrink-0 text-text-muted" />
            <div>
              <p className="text-sm font-medium text-heading">Secure Checkout</p>
              <p className="text-xs text-text-muted">Trusted payment partners</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-center sm:text-left">
            <Award size={24} className="flex-shrink-0 text-text-muted" />
            <div>
              <p className="text-sm font-medium text-heading">Top Brands</p>
              <p className="text-xs text-text-muted">Curated quality products</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/** Full-width product banner — Cybex-style hero block */
function ProductBanner({
  product,
  variant,
}: {
  product: Product;
  variant: "dark" | "light";
}) {
  const isDark = variant === "dark";
  return (
    <section className={isDark ? "bg-[#1A1A1A]" : "bg-[#F7F7F7]"}>
      <div className="mx-auto grid max-w-[1360px] items-center gap-8 px-6 py-16 lg:grid-cols-2 lg:gap-16 lg:px-10 lg:py-20">
        <div className={isDark ? "order-2 lg:order-1" : "order-2 lg:order-2"}>
          {product.category && (
            <span
              className={`text-xs font-medium uppercase tracking-widest ${
                isDark ? "text-white/50" : "text-[#717171]"
              }`}
            >
              {product.category}
            </span>
          )}
          <h2
            className={`mt-2 text-[32px] font-medium leading-tight tracking-tight lg:text-[40px] ${
              isDark ? "text-white" : "text-[#1A1A1A]"
            }`}
          >
            {product.name}
          </h2>
          {product.richDesc && (
            <p
              className={`mt-4 max-w-md text-[15px] leading-[1.7] line-clamp-3 ${
                isDark ? "text-white/60" : "text-[#717171]"
              }`}
            >
              {stripHtml(product.richDesc)}
            </p>
          )}
          {(() => {
            const highlights = [...(product.features ?? []), ...(product.benefits ?? [])].slice(0, 3);
            return highlights.length > 0 ? (
              <ul className="mt-4 space-y-1.5">
                {highlights.map((h, i) => (
                  <li
                    key={i}
                    className={`flex items-start gap-2 text-[13px] ${
                      isDark ? "text-white/50" : "text-[#717171]"
                    }`}
                  >
                    <Check size={14} className={`mt-0.5 flex-shrink-0 ${isDark ? "text-white/70" : "text-[#1A1A1A]"}`} />
                    <span>{typeof h === "string" ? h : String(h)}</span>
                  </li>
                ))}
              </ul>
            ) : null;
          })()}
          {product.price && (
            <p
              className={`mt-4 text-xl font-semibold ${
                isDark ? "text-white" : "text-[#1A1A1A]"
              }`}
            >
              {formatPrice(product.price)}
            </p>
          )}
          <Link
            to={`/product/${product.slug}`}
            className={`mt-8 inline-flex items-center gap-2 px-8 py-4 text-sm font-medium tracking-wide transition-colors ${
              isDark
                ? "bg-white text-[#1A1A1A] hover:bg-white/90"
                : "bg-[#1A1A1A] text-white hover:bg-[#333]"
            }`}
          >
            Discover
            <ArrowRight size={16} />
          </Link>
        </div>
        <div className={isDark ? "order-1 flex items-center justify-center lg:order-2" : "order-1 flex items-center justify-center lg:order-1"}>
          {product.imageUrl && (
            <img
              src={product.imageUrl}
              alt={product.name}
              className="max-h-[400px] w-full object-contain"
            />
          )}
        </div>
      </div>
    </section>
  );
}
